/**
 * Viewport + safe-area + chặn cử chỉ trình duyệt (core-loop 7.1, 7.3; contract r1-5).
 * Safe-area đọc qua CSS var `--sat/--sar/--sab/--sal` (mặc định = env(safe-area-inset-*)),
 * nên test có thể giả lập notch bằng cách đặt var trên :root.
 */
import type { Insets } from './layout.ts';

let probe: HTMLDivElement | null = null;

export function readSafeArea(): Insets {
  if (!probe) {
    probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText =
      'position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
      'padding:var(--sat) var(--sar) var(--sab) var(--sal);';
    document.body.appendChild(probe);
  }
  const cs = getComputedStyle(probe);
  const n = (v: string): number => Math.max(0, Math.round(parseFloat(v) || 0));
  return {
    top: n(cs.paddingTop),
    right: n(cs.paddingRight),
    bottom: n(cs.paddingBottom),
    left: n(cs.paddingLeft),
  };
}

export function viewportSize(): { width: number; height: number } {
  const vv = window.visualViewport;
  // Khi không zoom (đã chặn), visualViewport = layout viewport; lấy số nhỏ hơn cho an toàn
  // lúc bàn phím ảo/thanh công cụ Safari co màn.
  const w = Math.min(window.innerWidth, vv?.width ?? Infinity);
  const h = Math.min(window.innerHeight, vv?.height ?? Infinity);
  return { width: Math.round(w), height: Math.round(h) };
}

/** Gọi `cb` (gộp theo rAF) khi resize / xoay / visualViewport đổi. */
export function onViewportChange(cb: () => void): void {
  let pending = 0;
  const schedule = (): void => {
    if (pending) return;
    pending = requestAnimationFrame(() => {
      pending = 0;
      cb();
    });
  };
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  window.visualViewport?.addEventListener('resize', schedule);
  screen.orientation?.addEventListener?.('change', schedule);
}

/** Chặn pinch/double-tap zoom, kéo trang, bounce, menu nhấn giữ. */
export function blockBrowserGestures(): void {
  const stop = (e: Event): void => e.preventDefault();
  const opts: AddEventListenerOptions = { passive: false };
  for (const t of ['gesturestart', 'gesturechange', 'gestureend', 'dblclick']) {
    document.addEventListener(t, stop, opts);
  }
  document.addEventListener(
    'touchmove',
    (e) => {
      if (e.cancelable) e.preventDefault();
    },
    opts,
  );
  document.addEventListener(
    'touchstart',
    (e) => {
      if (e.touches.length > 1 && e.cancelable) e.preventDefault();
    },
    opts,
  );
  // Chạm đúp nhanh trên iOS cũ vẫn có thể zoom: chặn touchend thứ hai trong 300 ms.
  let lastEnd = 0;
  document.addEventListener(
    'touchend',
    (e) => {
      const now = e.timeStamp;
      if (now - lastEnd < 300 && e.cancelable && !(e.target instanceof HTMLButtonElement)) {
        e.preventDefault();
      }
      lastEnd = now;
    },
    opts,
  );
  window.addEventListener('scroll', () => {
    if (window.scrollX || window.scrollY) window.scrollTo(0, 0);
  });
}
